import AuthGuard from "components/authentication/AuthGuard";
import GuestGuard from "components/authentication/GuestGuard";
import DashboardLayout from "components/Layouts/DashboardLayout";
import Form from "components/crud/form";
import LoadingScreen from "components/LoadingScreen";
import useAuth from "hooks/useAuth";
import { FC, lazy, LazyExoticComponent, Suspense } from "react";
import { Navigate } from "react-router-dom";

const DashboardHome: FC = () => {
  const { user } = useAuth();
  // Admin users should go to admin area, not appointments/agenda
  if (user?.kind === "admin") {
    return <Navigate to="/dashboard/admin-reports" replace />;
  }
  // Non-admin users need a company to access appointments
  if (!user?.company_id) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to="/dashboard/appointments" replace />;
};

const Loadable = (Component: LazyExoticComponent<FC>) => (props: any) =>
(
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// authentication pages
const Login = Loadable(lazy(() => import("./pages/authentication/Login")));
const ResetPassword = Loadable(lazy(() => import("./pages/authentication/ResetPassword")));
const Register = Loadable(
  lazy(() => import("./pages/authentication/Register.jsx"))
);
const ForgetPassword = Loadable(
  lazy(() => import("./pages/authentication/ForgetPassword"))
);

// Outhes pages
const CompanyShowAppointment = Loadable(lazy(() => import("./pages/CompanyShowAppointment")));
const MeusAgendamentos = Loadable(lazy(() => import("./pages/MeusAgendamentos")));
const CompanyService = Loadable(lazy(() => import("./pages/CompanyService")));
const CompanyEmployee = Loadable(lazy(() => import("./pages/CompanyEmployee")));
const CompanyFinishedAppointment = Loadable(lazy(() => import("./pages/CompanyFinishedAppointment")));
const LandingPage = Loadable(lazy(() => import("./pages/LandingPage")));

// Dashboard pages
const Dashboard = Loadable(lazy(() => import("./pages/dashboards/Dashboard")));
const DashboardSummary = Loadable(lazy(() => import("./pages/dashboards/DashboardSummary")));
const Services = Loadable(lazy(() => import("./pages/dashboards/Services")));
const History = Loadable(lazy(() => import("./pages/dashboards/History")));
const Employees = Loadable(lazy(() => import("./pages/dashboards/Employees")));
const Appointments = Loadable(lazy(() => import("./pages/dashboards/Appointments")));
const CompaniesManagement = Loadable(lazy(() => import("./pages/dashboards/CompaniesManagement")));
const CompanyForm = Loadable(lazy(() => import("./pages/dashboards/CompanyForm")));
const AdminReports = Loadable(lazy(() => import("./pages/dashboards/AdminReports")));

// user profile
const UserProfile = Loadable(lazy(() => import("./pages/UserProfile")));

// error
const Error = Loadable(lazy(() => import("./pages/404")));

// routes
const routes = [
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "login",
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: "cadastro",
    element: (
      <GuestGuard>
        <Register />
      </GuestGuard>
    ),
  },
  {
    path: "forget-password",
    element: (
      <GuestGuard>
        <ForgetPassword />
      </GuestGuard>
    ),
  },
  {
    path: "reset-password",
    element: (
      <GuestGuard>
        <ResetPassword />
      </GuestGuard>
    ),
  },
  {
    path: "agendar/:slug",
    element: (
      <GuestGuard>
        <CompanyService />
      </GuestGuard>
    ),
  },
  {
    path: "horario/:uuid",
    element: (
      <GuestGuard>
        <CompanyShowAppointment/>
      </GuestGuard>
    ),
  },
  {
    path: "meus-agendamentos",
    element: <Navigate to="/" replace />,
  },
  {
    path: "agendar/:slug/meus-agendamentos",
    element: <MeusAgendamentos />,
  },
  {
    path: "agendar/:slug/services/:service_id",
    element: (
      <GuestGuard>
        <CompanyEmployee />
      </GuestGuard>
    ),
  },
  {
    path: "agendar/:slug/services/:service_id/users/:user_id",
    element: (
      <GuestGuard>
        <CompanyFinishedAppointment />
      </GuestGuard>
    ),
  },
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <DashboardHome />,
      },
      {
        path: "summary",
        element: <DashboardSummary />,
      },
      {
        path: "appointments",
        element: <Appointments />,
      },
      {
        path: "history",
        element: <History />,
      },
      {
        path: "services",
        element: <Services />,
      },
      {
        path: "users",
        element: <Employees />,
      },
      {
        path: "companies",
        element: <CompaniesManagement />,
      },
      {
        path: "companies/:id",
        element: <CompanyForm />,
      },
      {
        path: "admin-reports",
        element: <AdminReports />,
      },
      {
        path: ":routeDefault/list",
        element: <Services />,
      },
      {
        path: ":routeDefault/:id",
        element: <Form />,
      },
      {
        path: ":routeDefault/:create",
        element: <Form />,
      },
      {
        path: ":routeDefault/:id/:view",
        element: <Form />,
      },
      {
        path: "user-profile",
        element: <UserProfile />,
      },
    ],
  },
  {
    path: "agendar",
    element: (
        <Error />
    ),
    children: [
      {
        path: "",
        element: <Error />,
      },
      {
        path: "user-profile",
        element: <UserProfile />,
      },

    ],
  },
  {
    path: "*",
    element: <Error />
  },
];

export default routes;
